/**
 * Recipe Detail Page Functionality
 * Handles checkbox interactions and print functionality
 */

class RecipeDetail {
  constructor() {
    this.checkboxes = [];
    this.printButton = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.initializeCheckboxes();
    this.initializePrintButton();
    this.initializeKeyboardNavigation();
  }

  /**
   * Initialize checkbox functionality
   */
  initializeCheckboxes() {
    this.checkboxes = document.querySelectorAll('.ingredient-checkbox');

    this.checkboxes.forEach((checkbox, index) => {
      // Ensure checkboxes start unchecked (no persistence)
      checkbox.checked = false;

      // Add change event listener
      checkbox.addEventListener('change', (event) => {
        this.handleCheckboxChange(event, index);
      });

      // Add keyboard support
      checkbox.addEventListener('keydown', (event) => {
        this.handleCheckboxKeydown(event, index);
      });
    });
  }

  /**
   * Handle checkbox state changes
   * @param {Event} event - Checkbox change event
   * @param {number} index - Checkbox index
   */
  handleCheckboxChange(event, index) {
    const checkbox = event.target;
    const label = checkbox.closest('.ingredient-checkbox-label');
    const ingredientText = label.querySelector('.ingredient-text');

    if (checkbox.checked) {
      label.classList.add('ingredient-checked');
      checkbox.classList.add('is-checked'); // Add class for print mode
      ingredientText.setAttribute('aria-describedby', `checked-${index}`);

      // Add screen reader feedback
      this.announceToScreenReader(`Ingredient ${index + 1} checked`);
    } else {
      label.classList.remove('ingredient-checked');
      checkbox.classList.remove('is-checked'); // Remove class for print mode
      ingredientText.removeAttribute('aria-describedby');

      // Add screen reader feedback
      this.announceToScreenReader(`Ingredient ${index + 1} unchecked`);
    }
  }

  /**
   * Handle checkbox keyboard navigation
   * @param {Event} event - Keyboard event
   * @param {number} index - Current checkbox index
   */
  handleCheckboxKeydown(event, index) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextCheckbox(index);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousCheckbox(index);
        break;
      case ' ':
      case 'Enter':
        // Let the default behavior handle the toggle
        break;
    }
  }

  /**
   * Focus next checkbox in the list
   * @param {number} currentIndex - Current checkbox index
   */
  focusNextCheckbox(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.checkboxes.length;
    this.checkboxes[nextIndex].focus();
  }

  /**
   * Focus previous checkbox in the list
   * @param {number} currentIndex - Current checkbox index
   */
  focusPreviousCheckbox(currentIndex) {
    const prevIndex = currentIndex === 0 ? this.checkboxes.length - 1 : currentIndex - 1;
    this.checkboxes[prevIndex].focus();
  }

  /**
   * Initialize print button functionality
   */
  initializePrintButton() {
    this.printButton = document.querySelector('.recipe-download-btn');

    if (this.printButton) {
      this.printButton.addEventListener('click', (event) => {
        this.handlePrintClick(event);
      });
    }
  }

  /**
   * Handle print button click
   * Simple approach like COBS
   * @param {Event} event - Button click event
   */
  handlePrintClick(event) {
    event.preventDefault();
    window.print();
  }

  /**
   * Initialize keyboard navigation for the entire recipe
   */
  initializeKeyboardNavigation() {
    // Add skip links for better accessibility
    this.addSkipLinks();

    // Handle escape key to clear all checkboxes
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.clearAllCheckboxes();
      }
    });
  }

  /**
   * Add skip links for better keyboard navigation
   */
  addSkipLinks() {
    const recipeContainer = document.querySelector('.recipe-detail');
    if (!recipeContainer) return;

    const skipLinks = document.createElement('div');
    skipLinks.className = 'recipe-skip-links recipe-sr-only';
    skipLinks.innerHTML = `
      <a href="#recipe-ingredients" class="recipe-skip-link">Skip to ingredients</a>
      <a href="#recipe-method" class="recipe-skip-link">Skip to method</a>
    `;

    // Add focus styles for skip links
    const style = document.createElement('style');
    style.textContent = `
      .recipe-skip-link:focus {
        position: absolute;
        top: 10px;
        left: 10px;
        background: var(--color-accent);
        color: var(--color-background);
        padding: 8px 16px;
        border-radius: 4px;
        text-decoration: none;
        z-index: 9999;
        clip: auto;
        width: auto;
        height: auto;
        overflow: visible;
      }
    `;
    document.head.appendChild(style);

    recipeContainer.insertBefore(skipLinks, recipeContainer.firstChild);
  }

  /**
   * Clear all checkboxes (useful for reset functionality)
   */
  clearAllCheckboxes() {
    this.checkboxes.forEach((checkbox, index) => {
      if (checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change'));
      }
    });

    this.announceToScreenReader('All ingredients unchecked');
  }

  /**
   * Get checked ingredients count
   * @returns {number} Number of checked ingredients
   */
  getCheckedCount() {
    return Array.from(this.checkboxes).filter(checkbox => checkbox.checked).length;
  }

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'recipe-sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Public method to reset all checkboxes
   */
  reset() {
    this.clearAllCheckboxes();
  }

  /**
   * Public method to get recipe progress
   * @returns {Object} Progress information
   */
  getProgress() {
    const total = this.checkboxes.length;
    const checked = this.getCheckedCount();
    const percentage = total > 0 ? Math.round((checked / total) * 100) : 0;

    return {
      total,
      checked,
      percentage,
      remaining: total - checked
    };
  }
}

// Auto-initialize when DOM is ready
let recipeDetail = null;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    recipeDetail = new RecipeDetail();
  });
} else {
  recipeDetail = new RecipeDetail();
}

// Export for potential external use
window.RecipeDetail = RecipeDetail;
window.recipeDetail = recipeDetail;

// Additional utility functions for recipe functionality

/**
 * Copy recipe URL to clipboard
 * @param {string} url - URL to copy (defaults to current page)
 */
window.copyRecipeLink = function(url = window.location.href) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(url).then(() => {
      showCopySuccess();
    }).catch(() => {
      fallbackCopyTextToClipboard(url);
    });
  } else {
    fallbackCopyTextToClipboard(url);
  }
};

/**
 * Fallback copy method for older browsers
 * @param {string} text - Text to copy
 */
function fallbackCopyTextToClipboard(text) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
    showCopySuccess();
  } catch (err) {
    console.error('Failed to copy link:', err);
    showCopyError();
  }

  document.body.removeChild(textArea);
}

/**
 * Show copy success message
 */
function showCopySuccess() {
  const message = document.getElementById('copy-success-message');
  if (message) {
    message.textContent = 'Link copied to clipboard!';
    message.classList.add('show');

    setTimeout(() => {
      message.classList.remove('show');
    }, 3000);
  }
}

/**
 * Show copy error message
 */
function showCopyError() {
  const message = document.getElementById('copy-success-message');
  if (message) {
    message.textContent = 'Failed to copy link. Please try again.';
    message.classList.add('show');

    setTimeout(() => {
      message.classList.remove('show');
    }, 3000);
  }
}

/**
 * Simple print functionality like COBS
 */
window.printRecipe = function() {
  window.print();
};

// Handle browser back/forward navigation
window.addEventListener('popstate', function(event) {
  // Reset checkboxes when navigating back to the page
  if (recipeDetail) {
    recipeDetail.reset();
  }
});

// Handle page visibility changes (when user switches tabs)
document.addEventListener('visibilitychange', function() {
  if (document.visibilityState === 'visible' && recipeDetail) {
    // Optionally reset checkboxes when returning to the page
    // recipeDetail.reset();
  }
});

// Export utility functions
window.RecipeUtils = {
  copyLink: window.copyRecipeLink,
  print: window.printRecipe,
  getProgress: () => recipeDetail ? recipeDetail.getProgress() : null,
  reset: () => recipeDetail ? recipeDetail.reset() : null
};

